import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, liveEvents, upcomingEvents } from '@/mocks/common';

export default function CategoryPage() {
  const { slug = 'football' } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'live' | 'upcoming'>('live');
  const [activeSlug, setActiveSlug] = useState(slug);

  useEffect(() => {
    setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredLive = liveEvents.filter((e) => e.category === activeSlug);
  const filteredUpcoming = upcomingEvents.filter((e) => e.category === activeSlug);
  const displayEvents = activeTab === 'live' ? filteredLive : filteredUpcoming;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.bob-sports.com/' },
      { '@type': 'ListItem', position: 2, name: '赛事竞猜', item: 'https://www.bob-sports.com/category/football' },
      { '@type': 'ListItem', position: 3, name: currentCategory.name },
    ],
  };

  return (
    <>
      <SchemaOrg data={breadcrumbSchema} />
      <Navbar />

      <Breadcrumb
        items={[
          { label: '赛事竞猜', href: '/category/football' },
          { label: currentCategory.name },
        ]}
      />

      <main className="py-8 bg-background-50 min-h-screen">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Category Info */}
          <div className="bg-background-100 rounded-2xl border border-background-200/70 p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className={`${currentCategory.icon} text-2xl text-primary-500`}></i>
              </div>
              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
                  {currentCategory.name}竞猜
                </h1>
                <p className="text-sm text-foreground-500 leading-relaxed">{currentCategory.description}</p>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-foreground-500">今日赛事</span>
                <span className="text-2xl font-heading font-bold text-primary-500">{currentCategory.matchCount}</span>
                <span className="text-foreground-500">场</span>
              </div>
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-6 w-fit">
            <button
              onClick={() => setActiveTab('live')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'live'
                  ? 'bg-primary-500 text-white'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              进行中
            </button>
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'upcoming'
                  ? 'bg-primary-500 text-white'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              即将开赛
            </button>
          </div>

          {/* Events Grid */}
          {displayEvents.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayEvents.map((event) => (
                <div
                  key={event.id}
                  className="bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-primary-200 transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">
                      {event.league}
                    </span>
                    {activeTab === 'live' && 'time' in event ? (
                      <span className="flex items-center gap-1 text-xs text-red-500 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                        {(event as typeof liveEvents[0]).time}
                      </span>
                    ) : 'startTime' in event ? (
                      <span className="text-xs text-foreground-500">
                        {(event as typeof upcomingEvents[0]).startTime}
                      </span>
                    ) : null}
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-foreground-900">{event.homeTeam}</p>
                      <p className="text-sm font-semibold text-foreground-900 mt-1">{event.awayTeam}</p>
                    </div>
                    {activeTab === 'live' && 'homeScore' in event ? (
                      <div className="text-center px-3">
                        <div className="text-xl font-heading font-bold text-foreground-950">{(event as typeof liveEvents[0]).homeScore}</div>
                        <div className="text-xs text-foreground-400 my-0.5">-</div>
                        <div className="text-xl font-heading font-bold text-foreground-950">{(event as typeof liveEvents[0]).awayScore}</div>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex gap-2">
                    {event.drawOdds > 0 ? (
                      <>
                        <div className="flex-1 text-center py-1.5 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer">
                          <span className="text-xs text-foreground-500">主胜</span>
                          <span className="block text-sm font-heading font-semibold text-foreground-800">{event.homeOdds}</span>
                        </div>
                        <div className="flex-1 text-center py-1.5 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer">
                          <span className="text-xs text-foreground-500">平</span>
                          <span className="block text-sm font-heading font-semibold text-foreground-800">{event.drawOdds}</span>
                        </div>
                        <div className="flex-1 text-center py-1.5 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer">
                          <span className="text-xs text-foreground-500">客胜</span>
                          <span className="block text-sm font-heading font-semibold text-foreground-800">{event.awayOdds}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 text-center py-1.5 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer">
                          <span className="text-xs text-foreground-500">主队</span>
                          <span className="block text-sm font-heading font-semibold text-foreground-800">{event.homeOdds}</span>
                        </div>
                        <div className="flex-1 text-center py-1.5 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer">
                          <span className="text-xs text-foreground-500">客队</span>
                          <span className="block text-sm font-heading font-semibold text-foreground-800">{event.awayOdds}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-todo-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无{activeTab === 'live' ? '进行中' : '即将开赛'}的{currentCategory.name}赛事</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}